import React, { useState, useRef, useEffect } from "react";
import { Apartment, Person, Phone, Email, Language, Room, AccountBalance, Add, CallSplit, CallMerge, CheckCircle, Cancel, ErrorOutline, Delete } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";

const getIcon = (iconName) => {
  const icons = { Apartment, Person, Phone, Email, Language, Room, AccountBalance };
  return icons[iconName] || Apartment;
};

const preprocessTextLines = (lines, patterns) => {
  const processed = [];
  const contactPersonMatch = patterns.contact || [
    "Mr", "Mrs", "Ms", "Dr", "Shri", "Smt", "sharma", "singh", "bishnoi", "verma", "gupta", "yadav",
    "thakur", "chauhan", "mehta", "rathi", "bansal", "patel", "prajapati", "desai", "joshi", "shah",
    "gohil", "solanki", "jadeja", "g kirch", "suthar", "parmar", "reddy", "naidu", "iyer", "iyengar",
    "nair", "menon", "pillai", "shetty", "gowda", "das", "saha", "ghosh", "chatterjee", "chattopadhyay",
    "banerjee", "mukherjee", "bhattacharya", "sanyal"
  ];

  // Improved mobile number regex to handle various formats
  const mobileRegex = /(?:\+91[\s-]?)?(\d{10}|\d{3}[\s-]?\d{3}[\s-]?\d{4}|\d{2}[\s-]?\d{4}[\s-]?\d{4}|\d{3}[\s-]?\d{4}[\s-]?\d{3}|\d{5}[\s-]?\d{5})/g;
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const websiteRegex = /(https?:\/\/|www\.)[a-zA-Z0-9\-]+\.[a-z]{2,}(\S*)?/g;
  const labelPattern = /\b(Email|E-mail|Website|URL|Link)\b\s*[:\-]*/gi;
  const pincodeRegex = /\b\d{6}\b/;

  const addressKeywords = patterns.address || [
    "road", "street", "lane", "avenue", "cross", "junction", "bypass", "highway", "circle", "sector",
    "block", "phase", "area", "colony", "layout", "nagar", "market", "gali", "marg", "society",
    "building", "tower", "floor", "flat", "apartment", "office", "plaza", "park", "zone", "village",
    "city", "town", "district", "state", "country", "zip", "pincode", "postal", "mandal", "taluka",
    "bazaar", "chowk", "gram", "samiti", "bhavan", "corner", "opp.", "station", "chawl", "complex",
    "near", "odhav"
  ];
  const emailIndicators = patterns.email || [
    "info@", "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "co.in", "rocketmail.com",
    "protonmail.com", "icloud.com"
  ];
  const websiteKeywords = patterns.website || [
    "www", "http", "https", "website", "url", "link", "online", "web", "site", "internet", "digital",
    "portal", "domain", "homepage", "blog"
  ];

  let addressBuffer = [];
  let isBufferingAddress = false;
  let nameBuffer = [];
  let isBufferingName = false;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();

    if (/^F\s+[A-Za-z]/.test(line) || /^F\.\s*[A-Za-z]/.test(line)) {
      line = line.replace(/^F\s+|^F\.\s*/, "").trim();
    }

    const pinMatch = line.match(pincodeRegex);
    if (pinMatch) {
      if (isBufferingAddress && addressBuffer.length > 0) {
        processed.push(addressBuffer.join(", ").trim());
        addressBuffer = [];
        isBufferingAddress = false;
      }
      if (isBufferingName && nameBuffer.length > 0) {
        processed.push(nameBuffer.join(" ").trim());
        nameBuffer = [];
        isBufferingName = false;
      }
      processed.push(pinMatch[0]);
    }

    line = line.replace(labelPattern, "").trim();

    const emailMatches = [...line.matchAll(emailRegex)];
    if (emailMatches.length > 0) {
      if (isBufferingAddress && addressBuffer.length > 0) {
        processed.push(addressBuffer.join(", ").trim());
        addressBuffer = [];
        isBufferingAddress = false;
      }
      if (isBufferingName && nameBuffer.length > 0) {
        processed.push(nameBuffer.join(" ").trim());
        nameBuffer = [];
        isBufferingName = false;
      }
      emailMatches.forEach((match) => {
        const email = match[0];
        const parts = line.split(email);
        if (parts[0].trim()) processed.push(parts[0].trim());
        processed.push(email);
        line = parts[1]?.trim() || "";
      });
    }

    const websiteMatches = [...line.matchAll(websiteRegex)];
    if (websiteMatches.length > 0) {
      if (isBufferingAddress && addressBuffer.length > 0) {
        processed.push(addressBuffer.join(", ").trim());
        addressBuffer = [];
        isBufferingAddress = false;
      }
      if (isBufferingName && nameBuffer.length > 0) {
        processed.push(nameBuffer.join(" ").trim());
        nameBuffer = [];
        isBufferingName = false;
      }
      websiteMatches.forEach((match) => {
        const url = match[0];
        const parts = line.split(url);
        if (parts[0].trim()) processed.push(parts[0].trim());
        processed.push(url.trim());
        line = parts[1]?.trim() || "";
      });
    }

    const mobileMatches = [...line.matchAll(mobileRegex)];
    let foundMobile = false;
    for (const match of mobileMatches) {
      const rawMobile = match[0];
      const digitsOnly = rawMobile.replace(/\D/g, "");
      const spacingBetweenDigits = rawMobile.replace(/\d/g, "").length;
      const digitCount = digitsOnly.length;
      const averageSpacing = digitCount > 1 ? spacingBetweenDigits / (digitCount - 1) : 0;

      // Relaxed condition to capture more valid mobile numbers
      if (digitsOnly.length >= 10 && digitsOnly.length <= 12 && averageSpacing <= 2) {
        if (isBufferingAddress && addressBuffer.length > 0) {
          processed.push(addressBuffer.join(", ").trim());
          addressBuffer = [];
          isBufferingAddress = false;
        }
        if (isBufferingName && nameBuffer.length > 0) {
          processed.push(nameBuffer.join(" ").trim());
          nameBuffer = [];
          isBufferingName = false;
        }
        const cleanMobile = digitsOnly.slice(-10); // Take last 10 digits
        processed.push(cleanMobile);
        line = line.replace(rawMobile, "").trim();
        foundMobile = true;
      }
    }

    const lowerLine = line.toLowerCase();
    const isAddressLine = addressKeywords.some((keyword) => lowerLine.includes(keyword));

    if (isAddressLine && line) {
      if (isBufferingName && nameBuffer.length > 0) {
        processed.push(nameBuffer.join(" ").trim());
        nameBuffer = [];
        isBufferingName = false;
      }
      // Clean the address line to remove | and keep only alphanumeric, spaces, commas, periods, and hyphens
      const cleanedAddress = line.replace(/\|/g, "").replace(/[^a-zA-Z0-9\s,.-]/g, "").trim();
      if (cleanedAddress) {
        isBufferingAddress = true;
        addressBuffer.push(cleanedAddress);
      }
      continue;
    } else if (isBufferingAddress && addressBuffer.length > 0) {
      processed.push(addressBuffer.join(", ").trim());
      addressBuffer = [];
      isBufferingAddress = false;
    }

    const prefixes = ["Mr", "Mrs", "Ms", "Dr", "Shri", "Smt"];
    const cleanedName = line.replace(/[^a-zA-Z\s.]/g, "").trim();
    const words = cleanedName.split(/\s+/).filter(word => word);

    // Remove prefixes from the line
    let cleanedLine = cleanedName;
    for (const prefix of prefixes) {
      const prefixRegex = new RegExp(`^${prefix}\\.?\\s*|\\s+${prefix}\\.?\\s*`, "gi");
      cleanedLine = cleanedLine.replace(prefixRegex, " ").trim();
    }

    // Split into potential names
    let nameCandidates = [];
    const cleanedWords = cleanedLine.split(/\s+/).filter(word => word);
    if (cleanedWords.length >= 1 && cleanedWords.length <= 8 && cleanedWords.every(w => /^[A-Za-z.]+$/.test(w))) {
      const matchesDatabasePattern = cleanedWords.some(word => patterns.contact.some(pattern => word.toLowerCase() === pattern.toLowerCase()));
      if (matchesDatabasePattern) {
        if (cleanedWords.length <= 2) {
          // Single name (e.g., "Sureshbhai Shah")
          nameCandidates = [cleanedLine];
        } else {
          // Multiple names: build names by grouping 1-2 words
          let tempName = [];
          for (let i = 0; i < cleanedWords.length; i++) {
            tempName.push(cleanedWords[i]);
            const currentName = tempName.join(" ");
            const isValidName = tempName.length >= 1 && tempName.length <= 2 &&
              tempName.every(w => /^[A-Za-z.]+$/.test(w)) &&
              tempName.some(word => patterns.contact.some(pattern => word.toLowerCase() === pattern.toLowerCase()));
            if (isValidName && (i === cleanedWords.length - 1 || tempName.length === 2)) {
              nameCandidates.push(currentName);
              tempName = [];
            }
          }
          // If no valid names found, fall back to single name
          if (nameCandidates.length === 0 && cleanedLine) {
            nameCandidates = [cleanedLine];
          }
        }
      }
    }

    for (const candidate of nameCandidates) {
      const words = candidate.split(/\s+/);
      const isNamePattern = words.length >= 1 && words.length <= 2 && words.every((w) => /^[A-Za-z.]+$/.test(w));
      const matchesDatabasePattern = words.some(word => patterns.contact.some(pattern => word.toLowerCase() === pattern.toLowerCase()));
      if (isNamePattern && matchesDatabasePattern && candidate) {
        if (isBufferingAddress && addressBuffer.length > 0) {
          processed.push(addressBuffer.join(", ").trim());
          addressBuffer = [];
          isBufferingAddress = false;
        }
        processed.push(candidate); // Add cleaned name, e.g., Sureshbhai Shah or Hemal Shah
      }
    }

    if (line && !foundMobile) {
      processed.push(line);
    }
  }

  if (isBufferingAddress && addressBuffer.length > 0) {
    processed.push(addressBuffer.join(", ").trim());
    addressBuffer = [];
    isBufferingAddress = false;
  }

  if (isBufferingName && nameBuffer.length > 0) {
    processed.push(nameBuffer.join(" ").trim());
    nameBuffer = [];
    isBufferingName = false;
  }

  return processed;
};

const detectFieldCounts = (textLines, patterns) => {
  const counts = { mobileNumber: 0, email: 0, contactPersonName: 0 };

  const addressKeywords = patterns.address || ["road", "street", "lane", "avenue", "cross", "junction", "bypass", "highway", "circle", "sector", "block", "phase", "area", "colony", "layout", "nagar", "market", "gali", "marg", "society", "building", "tower", "floor", "flat", "apartment", "office", "plaza", "park", "zone", "village", "city", "town", "district", "state", "country", "zip", "pincode", "postal", "mandal", "taluka", "bazaar", "chowk", "gram", "samiti", "bhavan", "corner", "opp.", "station", "chawl", "complex", "near", "odhav", "stop"];
  const contactPersonMatch = patterns.contact || ["Mr", "Mrs", "Ms", "Dr", "Shri", "Smt", "Sharma", "Bishnoi", "Singh", "Verma", "Gupta", "Yadav", "Thakur", "Chauhan", "Mehta", "Rathi", "Bansal", "Patel", "Prajapati", "Desai", "Joshi", "Shah", "Gohil", "Solanki", "Jadeja", "G Kirch", "Suthar", "Parmar", "Reddy", "Naidu", "Iyer", "Iyengar", "Nair", "Menon", "Pillai", "Shetty", "Gowda", "Das", "Saha", "Ghosh", "Chatterjee", "Chattopadhyay", "Banerjee", "Bandopadhyay", "Mukherjee", "Mukhopadhyay", "Bhattacharya", "Bhattacharjee", "Sanyal"];
  const emailIndicators = patterns.email || ["info@", "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "co.in", "rocketmail.com", "protonmail.com", "icloud.com"];
  const websiteKeywords = patterns.website || ["www", "http", "https", "website", "url", "link", "online", "web", "site", "internet", "digital", "portal", "domain", "homepage", "blog"];

  textLines.forEach((line) => {
    const text = line.toLowerCase();
    const digits = line.replace(/\D/g, "");
    const isAddress = addressKeywords.some((keyword) => text.includes(keyword));
    const spacingBetweenDigits = line.replace(/\d/g, "").length;
    const digitCount = digits.length;
    const averageSpacing = digitCount > 1 ? spacingBetweenDigits / (digitCount - 1) : 0;

    if (/^\d{10}$/.test(digits) && !isAddress && averageSpacing <= 2) {
      counts.mobileNumber++;
    }
    if (/@/.test(text) && !isAddress) {
      counts.email++;
    }
    const isPersonName =
      /^(mr|mrs|dr|ms|shri|smt)\.?\s+[a-zA-Z. ]+$/i.test(line) ||
      (/^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(line) && !isAddress && !/@/.test(line) && !/(https?:\/\/|www\.)/.test(text) && !/\d{10}/.test(line));

    if (isPersonName) {
      counts.contactPersonName++;
    }
  });

  return counts;
};

const generateDynamicFieldConfigs = (textLines, patterns) => {
  const dynamicConfigs = [];

  dynamicConfigs.push({ key: "companyName", label: "Company", icon: "Apartment", placeholder: "Company", fieldType: "single" });
  dynamicConfigs.push(
    { key: "contactPersonName1", label: "Contact 1", icon: "Person", placeholder: "Contact 1", fieldType: "multi", baseKey: "contactPersonName" },
    { key: "contactPersonName2", label: "Contact 2", icon: "Person", placeholder: "Contact 2", fieldType: "multi", baseKey: "contactPersonName" }
  );
  dynamicConfigs.push(
    { key: "mobileNumber1", label: "Mob. 1", icon: "Phone", placeholder: "Mobile No. 1", fieldType: "multi", baseKey: "mobileNumber" },
    { key: "mobileNumber2", label: "Mob. 2", icon: "Phone", placeholder: "Mobile No. 2", fieldType: "multi", baseKey: "mobileNumber" }
  );
  dynamicConfigs.push(
    { key: "email1", label: "Email 1", icon: "Email", placeholder: "Email Address 1", fieldType: "multi", baseKey: "email" },
    { key: "email2", label: "Email 2", icon: "Email", placeholder: "Email Address 2", fieldType: "multi", baseKey: "email" }
  );
  dynamicConfigs.push({ key: "websiteUrl", label: "URL", icon: "Language", placeholder: "Website URL", fieldType: "single" });
  dynamicConfigs.push({ key: "address", label: "Address", icon: "AccountBalance", placeholder: "Address", fieldType: "single" });
  dynamicConfigs.push({ key: "pincode", label: "Pincode", icon: "Room", placeholder: "Pincode", fieldType: "single" });

  return dynamicConfigs;
};

const OCRTextAssignment = ({ extractedTextLines, businessCardData, onDataUpdate, onTextLinesUpdate, isProcessing, onSaveBusinessCard, onClose }) => {
  const [draggedIcon, setDraggedIcon] = useState(null);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [textAssignments, setTextAssignments] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [splitMode, setSplitMode] = useState(null);
  const inputRefs = useRef([]);
  const [editingField, setEditingField] = useState(null);
  const [singleCharEdit, setSingleCharEdit] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    title: "",
    message: "",
    type: "info",
  });
  const [showRawText, setShowRawText] = useState(false);
  const [patterns, setPatterns] = useState({});

  const showAlert = (title, message, type = "info") => {
    setAlertState({
      show: true,
      title,
      message,
      type,
    });
  };

  const hideAlert = () => {
    setAlertState({
      show: false,
      title: "",
      message: "",
      type: "info",
    });
  };

  useEffect(() => {
    const fetchPatterns = async () => {
      try {
        const response = await fetch('/patterns.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch patterns.json: ${response.statusText}`);
        }
        const data = await response.json();
        setPatterns(data);
      } catch (error) {
        console.error('Error fetching patterns:', error);
        showAlert('Error', 'Failed to fetch patterns from JSON file. Please ensure patterns.json exists in the public directory.', 'error');
      }
    };
    fetchPatterns();
  }, []);

  const savePatternsToJson = async (newPatterns) => {
    try {
      // Simulate saving to patterns.json
      // In a real app, you would need a backend to write to the file
      // For demonstration, we update patterns in state
      setPatterns(newPatterns);
      console.log('Patterns saved:', newPatterns);
      showAlert('Success', 'Patterns saved to JSON file.', 'success');
    } catch (error) {
      console.error('Error saving patterns:', error);
      showAlert('Error', 'Failed to save patterns to JSON file.', 'error');
    }
  };

  const initializeAssignments = () => {
    const preprocessed = preprocessTextLines(extractedTextLines, patterns);
    const dynamicConfigs = generateDynamicFieldConfigs(preprocessed, patterns);
    setFieldConfigs(dynamicConfigs);

    const initialAssignments = [];
    const updatedData = {
      companyName: "",
      contactPersonName1: "",
      contactPersonName2: "",
      mobileNumber1: "",
      mobileNumber2: "",
      email1: "",
      email2: "",
      websiteUrl: "",
      address: "",
      pincode: "",
    };

    const assignmentCounts = { mobileNumber: 0, email: 0, contactPersonName: 0 };

    const companyKeywords = patterns.company || [
      "solutions", "technologies", "pvt.ltd", "llp", "inc", "corp", "enterprises", "industries",
      "group", "associates", "services", "consulting", "org", "organization", "company", "infotech",
      "software", "systems", "co.", "clinic", "pattern", "engineering", "innovations", "dynamics",
      "ventures", "global", "labs", "consultoby", "systems", "partners", "management", "mechanics",
      "finance", "healthcare", "biotech", "pharma", "pharmaceuticals", "agency", "energy", "resources",
      "logistics", "collective"
    ];
    const addressKeywords = patterns.address || [
      "road", "street", "lane", "avenue", "cross", "junction", "bypass", "highway", "circle", "sector",
      "block", "phase", "area", "colony", "layout", "nagar", "market", "gali", "marg", "society",
      "building", "tower", "floor", "flat", "apartment", "office", "plaza", "park", "zone", "village",
      "city", "town", "district", "state", "country", "zip", "pincode", "postal", "mandal", "taluka",
      "bazaar", "chowk", "gram", "samiti", "bhavan", "corner", "opp.", "station", "chawl", "complex",
      "near", "odhav", "stop"
    ];
    const contactPersonMatch = patterns.contact || [
      "Mr", "Mrs", "Ms", "Dr", "Shri", "Smt", "sharma", "singh", "bishnoi", "verma", "gupta", "yadav",
      "thakur", "chauhan", "mehta", "rathi", "bansal", "patel", "prajapati", "desai", "joshi", "shah",
      "gohil", "solanki", "jadeja", "g kirch", "suthar", "parmar", "reddy", "naidu", "iyer", "Iyengar",
      "nair", "menon", "pillai", "shetty", "gowda", "das", "saha", "ghosh", "chatterjee", "chattopadhyay",
      "banerjee", "bandopadhyay", "mukherjee", "mukhopadhyay", "bhattacharya", "bhattacharjee", "sanyal"
    ];
    const emailIndicators = patterns.email || [
      "info@", "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "co.in", "rocketmail.com",
      "protonmail.com", "icloud.com"
    ];
    const websiteKeywords = patterns.website || [
      "www", "http", "https", "website", "url", "link", "online", "web", "site", "internet", "digital",
      "portal", "domain", "homepage", "blog"
    ];

    const orderedFieldKeys = [
      "companyName", "contactPersonName1", "contactPersonName2", "mobileNumber1", "mobileNumber2",
      "email1", "email2", "websiteUrl", "address", "pincode"
    ];

    orderedFieldKeys.forEach((fieldKey) => {
      initialAssignments.push({
        id: uuidv4(),
        text: "",
        assignedField: fieldKey,
        selected: false,
        matchSource: "initial",
      });
    });

    const assignedText = new Set();

    const validNames = [];
    for (const text of preprocessed) {
      if (!text) continue;
      const lowerText = text.toLowerCase();
      const words = text.split(/\s+/);
      const isPrefixName = contactPersonMatch.includes(words[0]?.toLowerCase());
      const isSurnameMatch = words.some(word => contactPersonMatch.includes(word.toLowerCase()));
      const isLogicPersonName =
        (isPrefixName || isSurnameMatch || /^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(text)) &&
        !addressKeywords.some((keyword) => lowerText.includes(keyword)) &&
        !/@/.test(lowerText) &&
        !/(https?:\/\/|www\.)/.test(lowerText) &&
        !/\d{10}/.test(text);
      if (isLogicPersonName && validNames.length < 2) {
        validNames.push(text);
      }
    }

    if (validNames.length >= 1) {
      const assignmentIndex1 = initialAssignments.findIndex(a => a.assignedField === "contactPersonName1");
      const cleanedName1 = validNames[0].replace(/[^a-zA-Z. ]/g, "").trim();
      initialAssignments[assignmentIndex1].text = cleanedName1;
      initialAssignments[assignmentIndex1].matchSource = "logic";
      updatedData.contactPersonName1 = cleanedName1;
      assignedText.add(validNames[0]);
      assignmentCounts.contactPersonName++;
    }
    if (validNames.length >= 2) {
      const assignmentIndex2 = initialAssignments.findIndex(a => a.assignedField === "contactPersonName2");
      const cleanedName2 = validNames[1].replace(/[^a-zA-Z. ]/g, "").trim();
      initialAssignments[assignmentIndex2].text = cleanedName2;
      initialAssignments[assignmentIndex2].matchSource = "logic";
      updatedData.contactPersonName2 = cleanedName2;
      assignedText.add(validNames[1]);
      assignmentCounts.contactPersonName++;
    }

    for (const fieldKey of orderedFieldKeys) {
      if (fieldKey.startsWith("contactPersonName")) continue;
      for (let i = 0; i < preprocessed.length; i++) {
        const text = preprocessed[i];
        if (!text || assignedText.has(text)) continue;

        const lowerText = text.toLowerCase();
        const originalText = text;

        if (fieldKey === "companyName") {
          const isLogicCompany = companyKeywords.some((k) => lowerText.includes(k.toLowerCase()));
          const isLikelyName = contactPersonMatch.includes(lowerText.split(/\s+/)[0]);
          const isLogicAddress = addressKeywords.some((keyword) => lowerText.includes(keyword.toLowerCase()));
          if (isLogicCompany && !isLikelyName && !isLogicAddress && !updatedData.companyName) {
            const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
            initialAssignments[assignmentIndex].text = originalText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
            initialAssignments[assignmentIndex].matchSource = "logic";
            updatedData.companyName = originalText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
            assignedText.add(originalText);
            continue;
          }
        }

        if (fieldKey.startsWith("mobileNumber")) {
          const raw = originalText.replace(/\s+/g, "");
          const digits = raw.replace(/\D/g, "");
          const digitCount = digits.length;
          const spacingBetweenDigits = raw.replace(/\d/g, "").length;
          const averageSpacing = digitCount > 1 ? spacingBetweenDigits / (digitCount - 1) : 0;
          const isLogicMobile = digitCount >= 10 && digitCount <= 12 && averageSpacing <= 2;
          if (isLogicMobile && assignmentCounts.mobileNumber < 2) {
            const mobileFieldKey = `mobileNumber${assignmentCounts.mobileNumber + 1}`;
            if (fieldKey === mobileFieldKey) {
              const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
              initialAssignments[assignmentIndex].text = digits.slice(-10);
              initialAssignments[assignmentIndex].matchSource = "logic";
              assignmentCounts.mobileNumber++;
              updatedData[mobileFieldKey] = digits.slice(-10);
              assignedText.add(originalText);
              continue;
            }
          }
        }

        if (fieldKey.startsWith("email")) {
          const isLogicEmail = /@/.test(lowerText) || emailIndicators.some((indicator) => lowerText.includes(indicator));
          if (isLogicEmail && assignmentCounts.email < 2) {
            const emailFieldKey = `email${assignmentCounts.email + 1}`;
            if (fieldKey === emailFieldKey) {
              const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
              initialAssignments[assignmentIndex].text = originalText;
              initialAssignments[assignmentIndex].matchSource = "logic";
              assignmentCounts.email++;
              updatedData[emailFieldKey] = originalText;
              assignedText.add(originalText);
              continue;
            }
          }
        }

        if (fieldKey === "websiteUrl") {
          const isLogicWebsite = /(https?:\/\/|www\.)[a-zA-Z0-9\-QX1%-+\.[a-z]{2,}([\/\w.-]*)*/.test(lowerText) ||
            websiteKeywords.some((keyword) => lowerText.includes(keyword));
          if (isLogicWebsite && !updatedData.websiteUrl) {
            const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
            initialAssignments[assignmentIndex].text = originalText;
            initialAssignments[assignmentIndex].matchSource = "logic";
            updatedData.websiteUrl = originalText;
            assignedText.add(originalText);
            continue;
          }
        }

        if (fieldKey === "address") {
          const isLogicAddress = addressKeywords.some((keyword) => lowerText.includes(keyword.toLowerCase()));
          const isOnlyDigits = /^\d+$/.test(originalText.trim());
          if (isLogicAddress && !isOnlyDigits && !updatedData.address) {
            let mergedAddress = [originalText];
            let pincodeSet = new Set();
            for (let j = i + 1; j < preprocessed.length; j++) {
              const nextLine = preprocessed[j]?.trim();
              if (!nextLine) break;
              const nextLower = nextLine.toLowerCase();
              if (addressKeywords.some(k => nextLower.includes(k)) || /^\d{6}$/.test(nextLine)) {
                mergedAddress.push(nextLine);
                if (/^\d{6}$/.test(nextLine)) {
                  pincodeSet.add(nextLine);
                } else {
                  assignedText.add(nextLine);
                }
              } else {
                break;
              }
            }
            let finalAddress = mergedAddress.join(" ").replace(/\s+/g, " ").trim();
            let lastPincodeWithContext = "";
            const pincodeRegex = /\b(\d{6})\s*(?:[A-Za-z,-]+\s*)?\s*$/;
            mergedAddress.forEach(line => {
              const match = line.match(pincodeRegex);
              if (match) {
                lastPincodeWithContext = line.trim();
              }
            });
            if (lastPincodeWithContext) {
              finalAddress = finalAddress.replace(/\b\d{6}\b/g, "").trim() + " " + lastPincodeWithContext;
            }
            finalAddress = finalAddress.replace(/\s+/g, " ").trim();
            const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
            initialAssignments[assignmentIndex].text = finalAddress;
            initialAssignments[assignmentIndex].matchSource = "logic";
            updatedData.address = finalAddress;
            assignedText.add(originalText);
            continue;
          }
        }

        if (fieldKey === "pincode") {
          const isExactPincode = /^\d{6}$/.test(originalText.trim());
          if (isExactPincode && !updatedData.pincode) {
            const assignmentIndex = initialAssignments.findIndex(a => a.assignedField === fieldKey);
            initialAssignments[assignmentIndex].text = originalText.trim();
            initialAssignments[assignmentIndex].matchSource = "logic";
            updatedData.pincode = originalText.trim();
            assignedText.add(originalText);
            continue;
          }
        }
      }
    }

    for (const text of preprocessed) {
      if (!text || assignedText.has(text)) continue;
      if (/^[^a-zA-Z0-9]+$/.test(text)) continue;
      initialAssignments.push({
        id: uuidv4(),
        text,
        assignedField: null,
        selected: false,
        matchSource: "unmatched",
      });
    }

    setTextAssignments(initialAssignments);
    onDataUpdate(updatedData);
  };

  useEffect(() => {
    if (extractedTextLines.length > 0 && Object.keys(patterns).length > 0) {
      initializeAssignments();
    }
  }, [extractedTextLines, patterns]);

  const handleDragStart = (e, fieldKey) => {
    setDraggedIcon(fieldKey);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", fieldKey);
  };

  const handleDragEnd = () => {
    setDraggedIcon(null);
  };

  const handleDrop = (e, textIndex) => {
    e.preventDefault();
    const fieldKey = e.dataTransfer.getData("text/plain");

    if (fieldKey && textAssignments[textIndex]) {
      const updatedAssignments = [...textAssignments];
      const multiFieldTypes = ["contactPersonName", "mobileNumber", "email"];
      const isMultiField = multiFieldTypes.some((type) => fieldKey.startsWith(type));

      if (isMultiField) {
        const baseKey = multiFieldTypes.find((type) => fieldKey.startsWith(type)) || fieldKey;
        const sameTypeAssignments = updatedAssignments.filter(
          (a) => a.assignedField && a.assignedField.toString().startsWith(baseKey)
        );

        if (sameTypeAssignments.length >= 2) {
          setDraggedIcon(null);
          return;
        }

        if (sameTypeAssignments.length === 0) {
          updatedAssignments[textIndex].assignedField = `${baseKey}1`;
        } else if (sameTypeAssignments.length === 1) {
          updatedAssignments[textIndex].assignedField = `${baseKey}2`;
          setTimeout(() => {
            const currentCounts = detectFieldCounts(updatedAssignments.map((a) => a.text), patterns);
            if (baseKey === "contactPersonName") currentCounts.contactPersonName = 2;
            if (baseKey === "mobileNumber") currentCounts.mobileNumber = 2;
            if (baseKey === "email") currentCounts.email = 2;
            const newConfigs = generateDynamicFieldConfigs(updatedAssignments.map((a) => a.text), patterns);
            setFieldConfigs(newConfigs);
          }, 0);
        }
      } else {
        const previousAssignmentIndex = updatedAssignments.findIndex(
          (a) => a.assignedField === fieldKey
        );
        if (previousAssignmentIndex !== -1 && previousAssignmentIndex !== textIndex) {
          updatedAssignments[previousAssignmentIndex].assignedField = null;
        }
        updatedAssignments[textIndex].assignedField = fieldKey;
      }

      const newData = { ...businessCardData };
      const assignedField = updatedAssignments[textIndex].assignedField;
      let newText = updatedAssignments[textIndex].text;

      if (assignedField && assignedField.startsWith("mobileNumber")) {
        newText = newText.replace(/\D/g, "").slice(-10);
      } else if (assignedField === "companyName") {
        newText = newText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
      } else if (assignedField.startsWith("contactPersonName")) {
        newText = newText.replace(/[^a-zA-Z. ]/g, "").trim();
      }

      if (assignedField) {
        newData[fieldKey] = newText;
        setTextAssignments(updatedAssignments);
        onDataUpdate(newData);
      }
      setDraggedIcon(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleTextChange = (index, newText) => {
    const updatedAssignments = [...textAssignments];
    const oldText = updatedAssignments[index].text;
    const charDifference = Math.abs(newText.length - oldText.length);

    updatedAssignments[index].text = newText;

    if (charDifference === 1) {
      setSingleCharEdit(false);
      setEditingField(index);
    } else {
      setSingleCharEdit(false);
      setEditingField(null);
    }

    const assignedField = updatedAssignments[index].assignedField;
    if (assignedField) {
      let cleanText = newText;
      if (assignedField.startsWith("mobileNumber")) {
        cleanText = cleanText.replace(/\D/g, "").slice(-10);
      } else if (assignedField === "companyName") {
        cleanText = cleanText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
      } else if (assignedField.startsWith("contactPersonName")) {
        cleanText = cleanText.replace(/[^a-zA-Z. ]/g, "").trim();
      }
      onDataUpdate({
        ...businessCardData,
        [assignedField]: cleanText,
      });
    }

    setTextAssignments(updatedAssignments);
  };

  const handleInputFocus = (index) => { };

  const handleInputBlur = (index) => { };

  const handleTextLinesReorder = (result) => {
    if (!result.destination) return;
    const assignments = [...textAssignments];
    const [movedItem] = assignments.splice(result.source.index, 1);
    assignments.splice(result.destination.index, 0, movedItem);
    setTextAssignments(assignments);
    const updatedLines = assignments.map((a) => a.text);
    onTextLinesUpdate(updatedLines);
  };

  const removeTextLine = (index) => {
    const updatedAssignments = textAssignments.filter((_, i) => i !== index);
    setTextAssignments(updatedAssignments);
  };

  const addNewTextLine = () => {
    const updatedAssignments = [
      ...textAssignments,
      {
        id: uuidv4(),
        text: "",
        assignedField: null,
        selected: false,
      },
    ];
    setTextAssignments(updatedAssignments);
  };

  const toggleLineSelection = (index) => {
    const updatedSelected = selectedLines.includes(index)
      ? selectedLines.filter((i) => i !== index)
      : [...selectedLines, index];
    setSelectedLines(updatedSelected);
  };

  const mergeSelectedLines = () => {
    if (selectedLines.length < 2) return;
    const sortedIndices = [...selectedLines].sort((a, b) => a - b);
    const firstIndex = sortedIndices[0];
    const mergedText = sortedIndices.map((i) => textAssignments[i].text).join(" ");
    const newAssignments = [...textAssignments];
    newAssignments[firstIndex].text = mergedText;

    const newData = { ...businessCardData };
    const assignedField = newAssignments[firstIndex].assignedField;
    if (assignedField) {
      let cleanText = mergedText;
      if (assignedField.startsWith("mobileNumber")) {
        cleanText = cleanText.replace(/\D/g, "").slice(-10);
      } else if (assignedField === "companyName") {
        cleanText = cleanText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
      } else if (assignedField.startsWith("contactPersonName")) {
        cleanText = cleanText.replace(/[^a-zA-Z. ]/g, "").trim();
      }
      newData[assignedField] = cleanText;
    }

    for (let i = sortedIndices.length - 1; i > 0; i--) {
      const removedIndex = sortedIndices[i];
      const removedField = newAssignments[removedIndex].assignedField;
      if (removedField) {
        newData[removedField] = "";
      }
      newAssignments.splice(removedIndex, 1);
    }

    setSelectedLines([]);
    setTextAssignments(newAssignments);
    onDataUpdate(newData);
  };

  const handleInputClick = (index, e) => {
    if (splitMode === index) {
      const input = e.currentTarget;
      const cursor = input.selectionStart || 0;
      const text = input.value;
      if (cursor > 0 && cursor < text.length) {
        const first = text.slice(0, cursor).trim();
        const second = text.slice(cursor).trim();
        if (first && second) {
          const newAssignments = [...textAssignments];
          const originalAssignedField = newAssignments[index].assignedField;
          newAssignments.splice(
            index,
            1,
            { id: uuidv4(), text: first, assignedField: originalAssignedField, selected: false },
            { id: uuidv4(), text: second, assignedField: null, selected: false }
          );

          const newData = { ...businessCardData };
          if (originalAssignedField) {
            let cleanText = first;
            if (originalAssignedField.startsWith("mobileNumber")) {
              cleanText = cleanText.replace(/\D/g, "").slice(-10);
            } else if (originalAssignedField === "companyName") {
              cleanText = cleanText.replace(/[^a-zA-Z .\-()&]/g, "").trim();
            } else if (originalAssignedField.startsWith("contactPersonName")) {
              cleanText = cleanText.replace(/[^a-zA-Z. ]/g, "").trim();
            }
            newData[originalAssignedField] = cleanText;
          }

          setTextAssignments(newAssignments);
          onDataUpdate(newData);
        }
      }
      setSplitMode(null);
    }
  };

  const enableSplitMode = (index) => {
    setSplitMode(index);
    setTimeout(() => {
      inputRefs.current[index]?.focus();
    }, 100);
  };

  const removeAssignment = (index) => {
    const updatedAssignments = [...textAssignments];
    const fieldKey = updatedAssignments[index].assignedField;
    updatedAssignments[index].assignedField = null;
    setTextAssignments(updatedAssignments);
    if (fieldKey) {
      const newData = { ...businessCardData };
      newData[fieldKey] = "";
      onDataUpdate(newData);
    }
  };

  const CustomAlert = () => {
    if (!alertState.show) return null;

    const getAlertIcon = () => {
      switch (alertState.type) {
        case "error":
          return <Cancel className="h-12 w-12 text-red-500" />;
        case "warning":
          return <ErrorOutline className="h-12 w-12 text-amber-500" />;
        case "success":
          return <CheckCircle className="h-12 w-12 text-emerald-500" />;
        default:
          return <ErrorOutline className="h-12 w-12 text-blue-500" />;
      }
    };

    const getAlertColors = () => {
      switch (alertState.type) {
        case "error":
          return { bg: "from-red-50 to-red-100", border: "border-red-300", button: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-red-500/25", text: "text-red-900", iconBg: "bg-red-100", accent: "bg-red-500" };
        case "warning":
          return { bg: "from-amber-50 to-amber-100", border: "border-amber-300", button: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-amber-500/25", text: "text-amber-900", iconBg: "bg-amber-100", accent: "bg-amber-500" };
        case "success":
          return { bg: "from-emerald-50 to-emerald-100", border: "border-emerald-300", button: "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-lg hover:shadow-emerald-500/25", text: "text-emerald-900", iconBg: "bg-emerald-100", accent: "bg-emerald-500" };
        default:
          return { bg: "from-blue-50 to-blue-100", border: "border-blue-300", button: "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-blue-500/25", text: "text-blue-900", iconBg: "bg-blue-100", accent: "bg-blue-500" };
      }
    };

    const colors = getAlertColors();

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-in fade-in duration-300">
        <div
          className={`bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border-2 ${colors.border} transform transition-all duration-300 scale-100 animate-in zoom-in-95 slide-in-from-bottom-4`}
        >
          <div className={`h-1 ${colors.accent} rounded-t-2xl`}></div>
          <div className={`bg-gradient-to-r ${colors.bg} p-8 rounded-t-2xl`}>
            <div className="flex items-start space-x-5">
              <div className={`${colors.iconBg} rounded-full p-3 shadow-lg`}> {getAlertIcon()} </div>
              <div className="flex-1">
                <h3 className={`md:text-2xl text-lg font-bold ${colors.text} mb-1`}> {alertState.title} </h3>
                <div className={`h-0.5 w-12 ${colors.accent} rounded-full`}></div>
              </div>
            </div>
          </div>
          <div className="p-8 pt-6">
            <p className="text-gray-700 d:text-lg text-base leading-relaxed whitespace-pre-line font-medium"> {alertState.message} </p>
          </div>
          <div className="px-8 pb-8 flex justify-end">
            <button
              onClick={hideAlert}
              className={`${colors.button} text-white px-8 py-3 rounded-xl font-semibold md:text-base text-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-offset-2 transform hover:scale-105 active:scale-95`}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isProcessing) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 md:text-base text-sm">Processing image...</p>
          </div>
        </div>
      </div>
    );
  }

  const orderedFieldKeys = [
    "companyName",
    "contactPersonName1",
    "contactPersonName2",
    "mobileNumber1",
    "mobileNumber2",
    "email1",
    "email2",
    "websiteUrl",
    "address",
    "pincode",
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 pt-0">
      <div className="pb-2" />
      {showRawText && (
        <div className="bg-gray-100 text-gray-700 p-4 rounded-lg mb-3">
          <h3 className="text-xs font-semibold mb-2 md:text-sm">Raw Extracted Text</h3>
          <pre className="whitespace-pre-wrap text-xs md:text-sm">
            {extractedTextLines.join("\n")}
          </pre>
        </div>
      )}

      <div className="mb-2 lg:col-span-3">
        <div className="flex flex-wrap gap-2.5 justify-center">
          {fieldConfigs.map((field) => {
            const IconComponent = getIcon(field.icon);
            return (
              <div
                key={field.key}
                draggable
                onDragStart={(e) => handleDragStart(e, field.key)}
                onDragEnd={handleDragEnd}
                className={`group relative flex items-center justify-center p-2 rounded-lg cursor-move transition-all duration-200 border-2 ${draggedIcon === field.key
                  ? "bg-blue-100 border-blue-500 shadow-lg scale-105"
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:scale-102"
                  }`}
              >
                <IconComponent
                  className={`h-5 w-5 ${draggedIcon === field.key ? "text-blue-600" : "text-gray-600"}`}
                  style={{ width: 18, height: 18 }}
                />
                <span
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold rounded-full shadow-lg opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 ease-out pointer-events-none whitespace-nowrap z-10"
                >
                  {field.label}
                  <span className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rotate-45" />
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-12">
          <div className="ml-auto">
            <button
              onClick={() => setShowRawText(!showRawText)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md mb-2 hover:bg-blue-700 transition-colors text-xs md:text-sm flex items-center ml-auto"
            >
              {showRawText ? "Hide Details" : "Show Details"}
            </button>
          </div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-gray-700 md:text-lg" />
            <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0 flex-wrap">
              {selectedLines.length >= 2 && (
                <button
                  onClick={mergeSelectedLines}
                  className="bg-orange-600 text-white px-3 py-1 rounded-md hover:bg-orange-700 transition-colors text-xs md:text-sm flex items-center"
                >
                  <CallMerge className="h-4 w-4 mr-1" />
                  Merge ({selectedLines.length})
                </button>
              )}
              <button
                onClick={addNewTextLine}
                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition-colors text-xs md:text-sm flex items-center"
              >
                <Add className="h-4 w-4 mr-1" />
                Add Line
              </button>
            </div>
          </div>

          <DragDropContext onDragEnd={handleTextLinesReorder}>
            <Droppable droppableId="textLines">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 flex-wrap"
                >
                  {textAssignments.map((assignment, index) => {
                    const fieldConfig = fieldConfigs.find(
                      (f) => f.key === assignment.assignedField
                    );
                    const AssignedIcon = fieldConfig ? getIcon(fieldConfig.icon) : null;

                    return (
                      <Draggable
                        key={assignment.id}
                        draggableId={`line-${assignment.id}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex flex-col p-3 rounded-lg border-2 transition-all duration-200 w-full sm:w-[calc(50%-0.75rem)] lg:w-full min-w-[250px] ${snapshot.isDragging
                              ? "bg-blue-50 border-blue-300 shadow-lg"
                              : draggedIcon
                                ? "border-dashed border-blue-400 bg-blue-50"
                                : selectedLines.includes(index)
                                  ? "border-orange-400 bg-orange-50"
                                  : splitMode === index
                                    ? "border-purple-400 bg-purple-50"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragOver={handleDragOver}
                          >
                            {AssignedIcon && (
                              <div className="md:hidden flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded-md mb-2 w-fit">
                                <AssignedIcon className="h-4 w-4 text-blue-600" />
                                <span className="text-[10px] text-blue-800 font-medium md:text-xs">
                                  {fieldConfig?.label}
                                </span>
                                <button
                                  onClick={() => removeAssignment(index)}
                                  className="text-blue-600 hover:text-blue-800 ml-1 text-[10px] md:text-xs"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedLines.includes(index)}
                                onChange={() => toggleLineSelection(index)}
                                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500 flex-none"
                              />
                              <div
                                {...provided.dragHandleProps}
                                className="flex-none p-1 cursor-move text-gray-500 hover:text-gray-700"
                                title="Drag to reorder"
                              >
                                <div className="w-px h-5 bg-gray-300" />
                              </div>
                              {AssignedIcon && (
                                <div className="hidden md:flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded-md flex-none">
                                  <AssignedIcon className="h-4 w-4 text-blue-600" style={{ width: 18, height: 18 }} />
                                  <span className="text-[10px] text-blue-800 font-medium md:text-xs">
                                    {fieldConfig?.label}
                                  </span>
                                  <button
                                    onClick={() => removeAssignment(index)}
                                    className="text-blue-600 hover:text-blue-800 ml-1 text-[10px] md:text-xs"
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                              <input
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                value={assignment.text}
                                onChange={(e) => handleTextChange(index, e.target.value)}
                                onClick={(e) => handleInputClick(index, e)}
                                onFocus={() => handleInputFocus(index)}
                                onBlur={() => handleInputBlur(index)}
                                className={`flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all min-w-0 text-xs md:text-sm ${splitMode === index
                                  ? "cursor-text bg-purple-50 border-purple-300"
                                  : singleCharEdit && editingField === index
                                    ? "bg-yellow-50 border-yellow-300"
                                    : ""
                                  }`}
                                placeholder={
                                  splitMode === index
                                    ? "Click where you want to split..."
                                    : singleCharEdit && editingField === index
                                      ? "Click again to continue editing..."
                                      : "Enter text..."
                                }
                                readOnly={singleCharEdit && editingField === index}
                              />
                              <div className="flex space-x-1 flex-none">
                                <button
                                  onClick={() => enableSplitMode(index)}
                                  className={`p-1 transition-colors ${splitMode === index
                                    ? "text-purple-700 bg-purple-100"
                                    : "text-purple-500 hover:text-purple-700"
                                    }`}
                                  title={splitMode === index ? "Click in text to split" : "Enable split mode"}
                                >
                                  <CallSplit className="h-4 w-4" style={{ width: 22, height: 22 }} />
                                </button>
                                <button
                                  onClick={() => removeTextLine(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Remove line"
                                >
                                  <Delete className="h-4 w-4" style={{ width: 22, height: 22 }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {splitMode !== null && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
              <p className="text-xs text-purple-700 font-medium md:text-sm">
                Split Mode Active: Click anywhere in the text box above to split at that position. Click the split icon again to cancel.
              </p>
            </div>
          )}

          {extractedTextLines.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm md:text-base">
              <p>No text extracted yet. Upload and process an image first.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-0 bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 md:text-xl">
          Contact Preview
        </h3>
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-6 space-y-2">
          {orderedFieldKeys
            .filter((fieldKey) => businessCardData[fieldKey]?.trim())
            .map((fieldKey) => {
              const fieldConfig = fieldConfigs.find((f) => f.key === fieldKey);
              const value = businessCardData[fieldKey];

              if (!fieldConfig) return null;

              const IconComponent = getIcon(fieldConfig.icon);

              return (
                <div key={fieldKey} className="flex items-center space-x-3 p-1 bg-white rounded-md">
                  <IconComponent className="h-4 w-4 text-gray-500" style={{ width: 18, height: 18 }} />
                  <span className="font-medium text-gray-600 min-w-0 flex-shrink-0 hidden md:block text-xs md:text-sm">
                    {fieldConfig.label}:
                  </span>
                  <span className="text-gray-800 truncate text-xs md:text-sm">{value}</span>
                </div>
              );
            })}
          {orderedFieldKeys.every((key) => !businessCardData[key]?.trim()) && (
            <div className="text-center py-4">
              <p className="text-white/80 text-xs md:text-sm">No data assigned yet</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-3">
        <button
          onClick={async () => {
            const {
              companyName,
              contactPersonName1,
              contactPersonName2,
              mobileNumber1,
              mobileNumber2,
              email1,
              email2,
              websiteUrl,
              address,
              pincode,
            } = businessCardData;

            const isCompanyNameValid = companyName?.trim();
            const isAnyContactPersonValid = contactPersonName1?.trim() || contactPersonName2?.trim();
            const isAnyMobileNumberValid = mobileNumber1?.trim() || mobileNumber2?.trim();

            if (!isCompanyNameValid || !isAnyContactPersonValid || !isAnyMobileNumberValid) {
              showAlert(
                "Required Fields Missing",
                "Please fill in Company, at least one Contact, and at least one Mobile No.",
                "warning"
              );
              return;
            }

            // Collect all words from businessCardData to save to JSON
            const newPatterns = {
              company: [...patterns.company],
              contact: [...patterns.contact],
              email: [...patterns.email],
              website: [...patterns.website],
              address: [...patterns.address],
            };

            if (companyName) {
              // Define stop words to exclude
              const stopWords = ['om', 'and', 'or', 'the', 'of', 'in', 'at', 'on'];

              // Split the company name into words, allowing special characters like periods
              const words = companyName
                .toLowerCase()
                .split(/\s+/)
                .filter(word => word && !/^[^a-zA-Z0-9]+$/.test(word));

              if (words.length > 1) {
                newPatterns.company.push(
                  ...words.slice(1).filter(word => !stopWords.includes(word) && word.length > 2)
                );
              } else if (words.length === 1 && /[a-zA-Z0-9]/.test(words[0])) {
                if (!stopWords.includes(words[0]) && words[0].length > 2) {
                  newPatterns.company.push(words[0]);
                }
              }
            }

            // Process address
            if (address) {
              // Define stop words to exclude for address
              const addressStopWords = ['and', 'or', 'the', 'of', 'in', 'at', 'on', 'ni', 'to', 'for'];

              // Split the address into words and apply initial filters
              const addressWords = address
                .toLowerCase()
                .split(/\s+/)
                .filter(word => word && !/^[^a-zA-Z0-9]+$/.test(word))
                .filter(word => !/^[a-zA-Z]+[-\/]\d+$/.test(word))
                .filter(word => !/^\d+$/.test(word));

              newPatterns.address.push(
                ...addressWords.filter(word => !addressStopWords.includes(word) && word.length > 2 && /[a-zA-Z]/.test(word))
              );
            }

            [contactPersonName1, contactPersonName2].forEach(name => {
              if (name) {
                const words = name.toLowerCase().split(/\s+/).filter(word => word);
                newPatterns.contact.push(...words);
              }
            });

            [email1, email2].forEach(email => {
              if (email) {
                const words = email.toLowerCase().split(/\s+/).filter(word => word);
                newPatterns.email.push(...words);
              }
            });

            if (websiteUrl) {
              const words = websiteUrl.toLowerCase().split(/\s+/).filter(word => word);
              newPatterns.website.push(...words);
            }

            try {
              const filteredPatterns = {
                company: [...new Set(newPatterns.company)],
                contact: [...new Set(newPatterns.contact)],
                email: [...new Set(newPatterns.email)],
                website: [...new Set(newPatterns.website)],
                address: [...new Set(newPatterns.address)],
              };
              await savePatternsToJson(filteredPatterns);
            } catch (error) {
              console.error('Error saving patterns:', error);
              showAlert('Error', 'Failed to save patterns.', 'error');
            }

            onSaveBusinessCard(businessCardData);
            onClose();
          }}
          className="bg-blue-600 text-white font-semibold px-4 py-1.5 rounded-lg hover:bg-blue-700 text-sm md:text-base"
        >
          Submit Data
        </button>
      </div>
      <CustomAlert />
    </div>
  );
};

export default OCRTextAssignment;